import { browserPdfOutput, type CashData, type CashFleet, type CashSource } from '@felix/ui';
import { api, officeApi } from './api.js';

const id = encodeURIComponent;

function query(values: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) if (value) params.set(key, value);
  const text = params.toString();
  return text ? `?${text}` : '';
}

/**
 * Ofiste kaptan kasası (ADR 0093): kasa gemi verisidir, ofis `cash.view` yetkili olduğu gemilerin
 * kasasını salt okunur görür. Ofis CTM talebine karar verir (`cash.approve`/`return`), ödemeyi
 * düzenler ve sayımı inceler (`cash.edit`); satırlar yalnız o gemiye iner.
 */
export function officeCashSource(): CashSource {
  return {
    scope: 'web',
    load: (q) => api<CashData>(`/office/cash${query(q)}`),
    pdf: browserPdfOutput(),
    // Hareketin makbuz dosyaları salt okunur; gemiden gelmişse açılır (ADR 0121 §2)
    attachments: (txnId) => officeApi.pmsAttachments(txnId, 'cash_transaction'),
    attachmentUrl: (attachmentId) => `/api/office/attachments/${id(attachmentId)}`,
    brand: async () => {
      try {
        const { branding } = await officeApi.branding();
        return branding?.name ? { name: branding.name, logo: branding.logo ?? null } : null;
      } catch {
        return null;
      }
    },
    office: {
      decide: async (ctmId, body) => {
        await api(`/office/cash/ctm/${id(ctmId)}/decision`, { method: 'POST', body });
      },
      transfer: async (ctmId, input) => {
        await api(`/office/cash/ctm/${id(ctmId)}/transfer`, { method: 'POST', body: { input } });
      },
      review: async (recId, decision, remarks) => {
        await api(`/office/cash/reconciliations/${id(recId)}/review`, {
          method: 'POST',
          body: { decision, remarks },
        });
      },
      // Gemi kasası: filo görünümü, kurulum ve kapama (ADR 0110)
      fleet: () => api<CashFleet>('/office/cash/accounts'),
      openAccounts: async (vesselId, currencies) => {
        await api('/office/cash/accounts', { method: 'POST', body: { vesselId, currencies } });
      },
      closeAccount: async (vesselId, currency, reason) => {
        await api('/office/cash/accounts/close', {
          method: 'POST',
          body: { vesselId, currency, reason },
        });
      },
    },
  };
}
